import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  type?: "website" | "article";
  image?: string;
  noindex?: boolean;
  structuredData?: object;
  keywords?: string;
}

const BASE_URL = "https://foundermitra.com";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = "Founder Mitra";

// Organization Schema - consistent across all pages
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BASE_URL}/#organization`,
  "name": "Founder Mitra",
  "alternateName": ["FounderMitra", "Founder Mitra India"],
  "url": BASE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": `${BASE_URL}/FounderMitra_logo.png`,
    "width": 512,
    "height": 512
  },
  "image": `${BASE_URL}/FounderMitra_logo.png`,
  "description": "FounderMitra is a strategic technology and AI advisory firm helping founder-led businesses make better decisions across technology strategy, AI adoption, product architecture, operational intelligence and execution governance.",
  "foundingDate": "2023",
  "founders": [{
    "@type": "Person",
    "name": "Founder Mitra Team"
  }],
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Kolkata",
    "addressRegion": "West Bengal",
    "addressCountry": "IN"
  },
  "areaServed": [
    {
      "@type": "Country",
      "name": "India"
    },
    {
      "@type": "GeoCircle",
      "name": "Global",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 22.5726,
        "longitude": 88.3639
      }
    }
  ],
  "serviceType": [
    "Strategic Technology Advisory",
    "Technology Leadership Advisory",
    "AI Advisory and Enablement",
    "Product Discovery and MVP Strategy",
    "Investor Technology Readiness",
    "Executive Dashboards and Decision Intelligence",
    "Platform Architecture and Scalability",
    "Workflow Automation and Agentic Systems"
  ],
  "knowsAbout": [
    "Startup Technology Strategy",
    "AI Enablement for Startups",
    "Product Development",
    "Technical Due Diligence",
    "Startup Scaling",
    "MVP Development",
    "Tech Stack Selection"
  ],
  "sameAs": [
    "https://www.linkedin.com/company/founder-mitra/",
    "https://www.instagram.com/foundermitra_real"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91-8293273236",
    "contactType": "customer service",
    "availableLanguage": ["English", "Hindi"],
    "areaServed": "IN"
  }
};

// Website Schema
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  "url": BASE_URL,
  "name": "Founder Mitra",
  "alternateName": "FounderMitra",
  "description": "Strategic tech and AI advisory for startup founders",
  "publisher": {
    "@id": `${BASE_URL}/#organization`
  },
  "inLanguage": "en-IN",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${BASE_URL}/insights?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

export function SEOHead({
  title,
  description,
  canonical,
  type = "website",
  image = DEFAULT_IMAGE,
  noindex = false,
  structuredData,
  keywords,
}: SEOHeadProps) {
  const fullCanonical = canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`;
  const fullTitle = title.includes("Founder Mitra") ? title : `${title} | Founder Mitra`;
  
  // Ensure description is within optimal length (140-160 chars)
  const metaDescription = description.length > 160 ? description.substring(0, 157) + "..." : description;

  // Combine all structured data
  const allStructuredData = [organizationSchema, websiteSchema];
  if (structuredData) {
    allStructuredData.push(structuredData as typeof organizationSchema);
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonical} />

      {/* Hreflang - single English site targeting all regions */}
      <link rel="alternate" hrefLang="x-default" href={fullCanonical} />
      <link rel="alternate" hrefLang="en" href={fullCanonical} />
      <link rel="alternate" hrefLang="en-IN" href={fullCanonical} />
      <link rel="alternate" hrefLang="en-US" href={fullCanonical} />
      <link rel="alternate" hrefLang="en-GB" href={fullCanonical} />
      <link rel="alternate" hrefLang="en-CA" href={fullCanonical} />
      <link rel="alternate" hrefLang="en-AU" href={fullCanonical} />
      <link rel="alternate" hrefLang="en-SG" href={fullCanonical} />
      <link rel="alternate" hrefLang="en-AE" href={fullCanonical} />
      <link rel="alternate" hrefLang="en-HK" href={fullCanonical} />

      {/* Language and Region */}
      <meta httpEquiv="content-language" content="en-IN" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="Kolkata, India" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:locale:alternate" content="en_GB" />
      <meta property="og:locale:alternate" content="en_CA" />
      <meta property="og:locale:alternate" content="en_AU" />
      <meta property="og:locale:alternate" content="en_SG" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO Meta Tags */}
      <meta name="author" content="Founder Mitra" />
      <meta name="publisher" content="Founder Mitra" />
      <meta name="copyright" content="Founder Mitra" />
      
      {/* Structured Data - one script tag per schema for better parsing */}
      {allStructuredData.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
}
